import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit7Notes: UnitNotes = {
  unitId: "unit-7",
  unitName: "Unit 7: Differential Equations",
  examWeight: "6–12%",
  focus: "Analysis of Functions (FUN)",
  sections: [
    {
      id: "topic-7-1",
      title: "Topic 7.1: Modeling Situations with Differential Equations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A <strong>Differential Equation</strong> is simply an equation that contains a derivative (e.g., <LatexText text="$\frac{dy}{dx} = 2x$" />). Solving a differential equation means finding the function <LatexText text="$y=f(x)$" /> whose derivative satisfies the equation.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Translating Verbal Statements to Math</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Real-world problems often describe how a quantity changes. You must translate these descriptions into differential equations.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800 mb-3">Key Phrases & Translations</h4>
              <div className="space-y-2 text-sm text-slate-700">
                <p><strong>"The rate of change of <LatexText text="$y$" />..."</strong> <LatexText text="$\rightarrow$" /> <LatexText text="$\frac{dy}{dt}$" /> (usually with respect to time).</p>
                <p><strong>"...is proportional to..."</strong> <LatexText text="$\rightarrow$" /> <LatexText text="$= k \cdot (\dots)$" /></p>
                <p><strong>"...is inversely proportional to..."</strong> <LatexText text="$\rightarrow$" /> <LatexText text="$= \frac{k}{(\dots)}$" /></p>
                <p><strong>"...increases..."</strong> <LatexText text="$\rightarrow$" /> Positive <LatexText text="$k$" /></p>
                <p><strong>"...decreases..."</strong> <LatexText text="$\rightarrow$" /> Negative <LatexText text="$k$" /></p>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-800 mb-3">Common Examples</h4>
              <div className="space-y-3 text-sm text-slate-700">
                <div>
                  <p><strong>Statement:</strong> "The rate of change of the population <LatexText text="$P$" /> is directly proportional to the square root of the population."</p>
                  <p className="mt-1"><strong>Equation:</strong> <LatexText text="$\frac{dP}{dt} = k\sqrt{P}$" /></p>
                </div>
                <div>
                  <p><strong>Statement:</strong> "The rate at which the object cools (<LatexText text="$T$" />) is proportional to the difference between the object's temperature and the room temperature (70)." (Newton's Law of Cooling)</p>
                  <p className="mt-1"><strong>Equation:</strong> <LatexText text="$\frac{dT}{dt} = k(T - 70)$" /></p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-2",
      title: "Topic 7.2: Verifying Solutions for Differential Equations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Just as you can check if <LatexText text="$x=3$" /> is a solution to <LatexText text="$2x+4=10$" /> by plugging it in, you can check if a function <LatexText text="$y = f(x)$" /> is a solution to a differential equation by plugging it <strong>and its derivative</strong> into the equation.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Procedure</h3>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Given a proposed solution</h4>
                  <p className="text-sm text-slate-600 mt-1">(e.g., <LatexText text="$y = e^{2x}$" />)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find the derivative</h4>
                  <p className="text-sm text-slate-600 mt-1">(e.g., <LatexText text="$\frac{dy}{dx} = 2e^{2x}$" />)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Substitute into the differential equation</h4>
                  <p className="text-sm text-slate-600 mt-1">Plug both <LatexText text="$y$" /> and <LatexText text="$\frac{dy}{dx}$" /> into the given equation</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Check equality</h4>
                  <p className="text-sm text-slate-600 mt-1">If Left Side = Right Side, it is a solution</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">General vs. Particular Solutions</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">General Solution</h4>
                <p className="text-sm text-slate-700 mt-1">Contains a constant of integration "<LatexText text="$+C$" />" (e.g., <LatexText text="$y = x^2 + C$" />). It represents a <em>family</em> of functions.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Particular Solution</h4>
                <p className="text-sm text-slate-700 mt-1">Has a specific numerical value for <LatexText text="$C$" /> (e.g., <LatexText text="$y = x^2 + 5$" />). This specific curve passes through a given <strong>Initial Condition</strong> <LatexText text="$(x_0, y_0)$" />.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-3",
      title: "Topic 7.3: Sketching Slope Fields",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">What is a Slope Field?</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A slope field is a graphical tool to visualize the solution to a differential equation without solving it algebraically. It is a "map" of the flow of the function.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              At every point <LatexText text="$(x, y)$" /> on a grid, the differential equation <LatexText text="$\frac{dy}{dx}$" /> tells you the <strong>slope</strong> of the tangent line at that exact spot.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">How to Sketch One</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              On the AP Exam, you are usually given a grid of 9 to 12 dots.
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Pick a point on the grid</h4>
                  <p className="text-sm text-slate-600 mt-1">(e.g., <LatexText text="$(1, 2)$" />)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Plug coordinates into the differential equation</h4>
                  <p className="text-sm text-slate-600 mt-1">Calculate the slope value. Example: If <LatexText text="$\frac{dy}{dx} = x + y$" />, at <LatexText text="$(1, 2)$" />, the slope is <LatexText text="$1+2=3$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Draw a short line segment</h4>
                  <p className="text-sm text-slate-600 mt-1">At that point with that steepness</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Repeat for all indicated points</h4>
                </div>
              </div>
            </div>

            <ImportantNote>
              <strong>AP Exam Tips:</strong>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><strong>Zero Slope:</strong> If the slope is 0, draw a horizontal segment.</li>
                <li><strong>Undefined Slope:</strong> If the derivative is undefined (division by zero), <strong>leave the spot blank</strong>. Do not draw a vertical line.</li>
              </ul>
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-4",
      title: "Topic 7.4: Reasoning Using Slope Fields",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Matching Equations to Slope Fields</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You must be able to look at a slope field and identify which differential equation generated it.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800 mb-3">Strategies</h4>
              <div className="space-y-3 text-sm text-slate-700">
                <div>
                  <p><strong>Look for Independence:</strong></p>
                  <ul className="list-disc pl-5 space-y-1 mt-1">
                    <li>If the slopes are the same across every row (left-to-right), the slope depends only on <LatexText text="$y$" /> (e.g., <LatexText text="$\frac{dy}{dx} = y$" />).</li>
                    <li>If the slopes are the same up and down every column, the slope depends only on <LatexText text="$x$" /> (e.g., <LatexText text="$\frac{dy}{dx} = \cos x$" />).</li>
                  </ul>
                </div>
                <div>
                  <p><strong>Look for Zero Slopes:</strong> Where are the flat lines?</p>
                  <ul className="list-disc pl-5 space-y-1 mt-1">
                    <li>If slopes are 0 along the x-axis (<LatexText text="$y=0$" />), the numerator likely has a <LatexText text="$y$" />.</li>
                    <li>If slopes are 0 along the line <LatexText text="$y=x$" />, the equation might be <LatexText text="$\frac{dy}{dx} = x - y$" />.</li>
                  </ul>
                </div>
                <div>
                  <p><strong>Look for Positive/Negative Regions:</strong> Check a quadrant. If the slopes in Quadrant I are negative, the equation might involve a negative sign.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sketching Solution Curves</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You may be asked to "sketch the solution curve that passes through the point <LatexText text="$(1, 1)$" />."
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Start at the given initial point</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$(1, 1)$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">"Go with the flow"</h4>
                  <p className="text-sm text-slate-600 mt-1">Trace a continuous curve that follows the direction of the slope lines</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Extend the curve</h4>
                  <p className="text-sm text-slate-600 mt-1">To the edges of the graph window</p>
                </div>
              </div>
            </div>

            <ImportantNote>
              <strong>Crucial:</strong> Do <strong>not</strong> cross asymptote lines or regions where the slope is undefined.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-6",
      title: "Topic 7.6: Finding General Solutions Using Separation of Variables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Importance of this Topic</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This is the standard "5-to-6 point" Free Response Question in Unit 7. You generally cannot earn <em>any</em> points if you fail the very first step (Separation).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Procedure (Separation of Variables)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To solve <LatexText text="$\frac{dy}{dx} = f(x)g(y)$" />:
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Separate</h4>
                  <p className="text-sm text-slate-600 mt-1">Use Algebra (multiplication/division) to move all <LatexText text="$y$" /> terms (including <LatexText text="$dy$" />) to the left and all <LatexText text="$x$" /> terms (including <LatexText text="$dx$" />) to the right.</p>
                  <p className="text-sm text-slate-600 mt-2"><strong>Format:</strong> <LatexText text="$g(y) \, dy = f(x) \, dx$" /></p>
                  <p className="text-sm text-slate-600 mt-2"><strong>Warning:</strong> You cannot subtract terms to separate them. You must multiply or divide.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Integrate</h4>
                  <p className="text-sm text-slate-600 mt-1">Draw an integral symbol <LatexText text="$\int$" /> on both sides. Integrate each side independently.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Add C</h4>
                  <p className="text-sm text-slate-600 mt-1">Add a <LatexText text="$+C$" /> to the right side (x-side) immediately after integrating.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-7",
      title: "Topic 7.7: Finding Particular Solutions Using Initial Conditions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding C</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Once you have the General Solution (with <LatexText text="$+C$" />):
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Substitute the initial condition</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$(x, y)$" /> given in the problem into your integrated equation</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Solve for C</h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Plug the value of C back</h4>
                  <p className="text-sm text-slate-600 mt-1">Into the equation</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Solving for y (Explicit Solution)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Most AP questions ask for the solution in the form "<LatexText text="$y = \dots$" />".
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Use algebra to isolate y</h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Exponentiation</h4>
                  <p className="text-sm text-slate-600 mt-1">If you have <LatexText text="$\ln|y| = x + C$" />, exponentiate both sides to get <LatexText text="$|y| = e^{x+C} = e^C \cdot e^x$" />.</p>
                  <p className="text-sm text-slate-600 mt-2">This simplifies to <LatexText text="$y = Ae^x$" /> (where <LatexText text="$A = \pm e^C$" />).</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Domain of the Solution (Advanced but Required)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A solution to a differential equation must be a <strong>continuous function</strong> on an <strong>open interval</strong> containing the initial condition.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              If the solution has a vertical asymptote (e.g., <LatexText text="$y = \frac{1}{x-2}$" />) and the initial condition is <LatexText text="$x=3$" />, the domain is <LatexText text="$(2, \infty)$" />. You cannot include the other side of the asymptote.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-8",
      title: "Topic 7.8: Exponential Models with Differential Equations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Proportionality Model</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This is the most common differential equation in nature: "The rate of change of a quantity <LatexText text="$y$" /> is proportional to the quantity itself."
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$\frac{dy}{dt} = ky$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Solution (Memorize This)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you separate and solve the equation above, you always get:
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <LatexText text="$$y = y_0 e^{kt}$$" />
            </div>
            <div className="space-y-2">
              <p className="text-slate-700"><strong><LatexText text="$y_0$" /> (or <LatexText text="$C$" />):</strong> The initial amount at <LatexText text="$t=0$" />.</p>
              <p className="text-slate-700"><strong><LatexText text="$k$" />:</strong> The growth (if <LatexText text="$k>0$" />) or decay (if <LatexText text="$k<0$" />) constant.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Applications</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Population Growth</h4>
                <p className="text-sm text-slate-700 mt-1">Use positive <LatexText text="$k$" />.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Radioactive Decay</h4>
                <p className="text-sm text-slate-700 mt-1">Use negative <LatexText text="$k$" />.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Continuously Compounded Interest</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$A = Pe^{rt}$" /> (same mathematical structure).</p>
              </div>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> If a multiple-choice question says "rate of change is proportional to amount," you can jump straight to the solution <LatexText text="$y = Ce^{kt}$" /> without integrating, saving valuable time.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "summary-7",
      title: "Summary of Key Skills for Unit 7",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Separation</h4>
                  <p className="text-sm text-slate-700 mt-1">Getting <LatexText text="$y$" />'s with <LatexText text="$dy$" /> and <LatexText text="$x$" />'s with <LatexText text="$dx$" /> is the gatekeeper skill. If you don't do this, you get 0 points on the FRQ.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Visualizing</h4>
                  <p className="text-sm text-slate-700 mt-1">Reading slope fields to predict the shape of the graph.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Algebraic Precision</h4>
                  <p className="text-sm text-slate-700 mt-1">Handling <LatexText text="$+C$" /> correctly, especially when dealing with natural logs (<LatexText text="$\ln$" />) and absolute value bars.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Modeling</h4>
                  <p className="text-sm text-slate-700 mt-1">Recognizing that <LatexText text="$\frac{dy}{dt} = ky$" /> means exponential growth/decay.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default calculusAbUnit7Notes;